/**
 * HistopathSpecimen – Sample collection dialog (child of SpecimenCollection).
 * Migrated from admin4.0 HistopathSpecimen.js.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { CommonPatientDetails } from "../../Components/Common Components/CommonComponents";
import Loader from "../../Components/Loader";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import "../../Styles/Configuration.css";

const sampleTypeOptions = [
  { value: "New", label: "New" },
  { value: "Existing", label: "Existing" },
];

export default function HistopathSpecimen({ open, onClose, onSaveSuccess, errorMessage, patientId, billId, selectedDate }) {
  const [specimenList, setSpecimenList] = useState([]);
  const [testData, setTestData] = useState([]);
  const [patientInfo, setPatientInfo] = useState({});
  const [billSummary, setBillSummary] = useState(null);
  const [collection, setCollection] = useState(null);
  const [commonLocation, setCommonLocation] = useState("");
  const [existingSamples, setExistingSamples] = useState({});
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [savedTests, setSavedTests] = useState(new Set());
  const [isloader, setisloader] = useState(false);
  const [message, setMessage] = useState({
    isErrorMsgText: "",
    successMsgText: "",
    isErrorMsg: false,
    successMsg: false,
  });

  const successMessage = useCallback((msg) => {
    setMessage((prev) => ({ ...prev, successMsg: true, successMsgText: msg }));
  }, []);

  const msgClose = useCallback(() => {
    setMessage((prev) => ({
      ...prev,
      isErrorMsg: false,
      isErrorMsgText: "",
      successMsg: false,
      successMsgText: "",
    }));
  }, []);

  const getSpecimenList = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SPECIMEN + "?is_active=true")
      .then((response) => {
        if (response?.data?.status === "success") {
          setSpecimenList(response.data.data?.filter((item) => item?.is_active) || []);
        } else {
          errorMessage?.(response?.data?.message);
        }
      })
      .catch((e) => {
        errorMessage?.(e?.response?.data?.message ?? e?.message);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- errorMessage omitted to avoid loop when parent shows error
  }, []);

  const getSampleCollectionDetailByBillId = useCallback(() => {
    if (!billId) {
      errorMessage?.("Bill ID is required");
      return;
    }
    setisloader(true);
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SAMPLE_COLLECTION_DETAIL + `?bill_id=${billId}`)
      .then((response) => {
        if (response?.data?.status === "success") {
          const d = response.data.data || {};
          const pi = d.patient_information || {};
          setPatientInfo({
            patient_id: pi.patient_id,
            name: pi.name,
            age: pi.age,
            patient_identifier: pi.patient_identifier,
            mobile_no: pi.mobile_no,
            patient_account_number: d?.bill_summary?.nxt_id,
            patients_mobile: pi.mobile_no,
          });
          setCommonLocation(d?.collection?.location || "")
          setBillSummary(d.bill_summary || null);
          setCollection(d?.collection || null);
          const lineItems = d.line_items || [];
          const collectionOptions = d?.collection?.laboratory_options || [];
          const tests = lineItems.map((item) => ({
            bill_line_item_id: item.bill_line_item_id,
            test_name: item.test_name,
            specimen_id: d?.collection?.specimen_id || "",
            specimen_type: d?.collection?.specimen_type || "",
            specimen_code: d?.collection?.specimen_code || "",
            sample_type: d?.collection?.sample_type_default === "new" ? "New" : "Existing",
            sample_id: d?.collection?.sample_id || "",
            overall_specimen_sequence: (d?.collection?.overall_specimen_sequence ?? d?.bill_summary?.nxt_id) || "",
            location: (item.location ?? d?.collection?.location) || "",
            laboratory_options: item.laboratory_options ?? collectionOptions,
            select_is_outsource: item.laboratory_type === "outsource" ? "Outsource" : item.laboratory_type === "self" ? "Self" : undefined,
            is_outsource: item.laboratory_type === "outsource",
            specimen_data: item.specimen_id ? { specimen_type: item.specimen_type, specimen_code: item.specimen_code } : null,
          }));
          const testDatas = tests.length ? [{ tests }] : [];
          setTestData(testDatas);
          const initialSelected = new Set();
          tests.forEach((test, testIndex) => {
            if (test.specimen_id) initialSelected.add(`0-${testIndex}`);
          });
          setSelectedTests(initialSelected);
        } else {
          errorMessage?.(response?.data?.message);
          setTestData([]);
          setPatientInfo({});
          setBillSummary(null);
          setCollection(null);
        }
      })
      .catch((e) => {
        errorMessage?.(e?.response?.data?.message ?? e?.message);
        setTestData([]);
        setPatientInfo({});
        setBillSummary(null);
        setCollection(null);
      })
      .finally(() => setisloader(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- errorMessage omitted to avoid effect loop on API failure
  }, [billId]);

  const getSampleCollectionData = useCallback(() => {
    if (!patientId) {
      errorMessage?.("Patient ID is required");
      return;
    }
    setisloader(true);
    let formattedDate = "";
    if (selectedDate) {
      const date = new Date(selectedDate);
      formattedDate = date.toISOString().split("T")[0];
    }
    let queryString = `?patient_id=${patientId}`;
    if (formattedDate) queryString += `&date=${formattedDate}`;

    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SAMPLE_DETAILS + queryString)
      .then((response) => {
        if (response?.data?.status === "success") {
          const testDatas = response.data.test_datas || [];
          setTestData(testDatas);
          setPatientInfo(response.data.patient_data || {});
          setCollection(null);
          const initialSelected = new Set();
          testDatas.forEach((invoice, invoiceIndex) => {
            invoice.tests?.forEach((test, testIndex) => {
              const testKey = `${invoiceIndex}-${testIndex}`;
              if (test.specimen_id) initialSelected.add(testKey);
            });
          });
          setSelectedTests(initialSelected);
        } else {
          errorMessage?.(response?.data?.message);
          setTestData([]);
          setPatientInfo({});
        }
      })
      .catch((e) => {
        errorMessage?.(e?.response?.data?.message ?? e?.message);
        setTestData([]);
        setPatientInfo({});
      })
      .finally(() => setisloader(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- errorMessage omitted to avoid effect loop on API failure
  }, [patientId, selectedDate]);

  const handleBarcodePrint = useCallback(
    (barcodeId) => {
      if (barcodeId == null || barcodeId === "") {
        errorMessage?.("Cannot print barcode: missing sample collection record id.");
        return;
      }
      const url = `${Serviceurls.HISTOPATHOLOGY_SPECIMEN_BARCODE}?barcode_id=${encodeURIComponent(barcodeId)}`;
      RestAPIService.PrintPDF(url)
        .then((response) => {
          const file = new Blob([response.data], { type: "application/pdf" });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
        })
        .catch((err) => {
          errorMessage?.(err?.response?.data?.message || err?.message || "Failed to load barcode print.");
        });
    },
    [errorMessage]
  );

  const getExistingSamples = useCallback(
    (specimenId) => {
      const pid = patientId || patientInfo?.patient_id;
      if (!specimenId || !pid) return;
      RestAPIService.getAll(
        Serviceurls.HISTOPATHOLOGY_SAMPLE_LIST + `?patient_id=${pid}&specimen_id=${specimenId}`
      )
        .then((response) => {
          if (response?.data?.status === "success") {
            setExistingSamples((prev) => ({ ...prev, [specimenId]: response.data.data || [] }));
          } else {
            errorMessage?.(response?.data?.message);
          }
        })
        .catch((e) => errorMessage?.(e?.response?.data?.message ?? e?.message));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- errorMessage omitted to avoid effect loop
    [patientId, patientInfo?.patient_id]
  );

  useEffect(() => {
    if (open && billId) {
      getSpecimenList();
      getSampleCollectionDetailByBillId();
    } else if (open && patientId) {
      getSpecimenList();
      getSampleCollectionData();
    } else {
      setTestData([]);
      setPatientInfo({});
      setBillSummary(null);
      setCollection(null);
      setCommonLocation("");
      setExistingSamples({});
      setSelectedTests(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch when open/patientId/billId change; omit fetch fns to prevent loop on API failure
  }, [open, patientId, billId]);

  const isTestSelected = (invoiceIndex, testIndex) => selectedTests.has(`${invoiceIndex}-${testIndex}`);
  const hasExistingSpecimen = (test) => !!test?.sample_collected;
  const isCheckboxDisabled = (test, invoiceIndex, testIndex) => {
    const testKey = `${invoiceIndex}-${testIndex}`;
    return savedTests.has(testKey) || (test?.specimen_id && test?.sample_collected);
  };

  const handleSampleTypeChange = (invoiceIndex, testIndex, value) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    if (hasExistingSpecimen(test)) return;
    const updatedTestData = [...testData];
    const t = updatedTestData[invoiceIndex].tests[testIndex];
    t.sample_type = value;
    if (value === "Existing" && t.specimen_id) {
      getExistingSamples(t.specimen_id);
      t.sample_id = null;
      t.location = null;
      t.overall_specimen_sequence = null;
    } else if (value === "New") {
      t.sample_id = null;
      t.location = null;
      t.overall_specimen_sequence = null;
    }
    setTestData(updatedTestData);
  };

  const handleSpecimenChange = (invoiceIndex, testIndex, newValue) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    if (hasExistingSpecimen(test)) return;
    const updatedTestData = [...testData];
    const t = updatedTestData[invoiceIndex].tests[testIndex];
    t.specimen_id = newValue?.id ?? null;
    t.specimen_data = newValue;
    if (t.sample_type === "Existing" && newValue?.id) {
      getExistingSamples(newValue.id);
      t.sample_id = null;
      t.location = null;
      t.overall_specimen_sequence = null;
    }
    setTestData(updatedTestData);
  };

  const handleSampleIdChange = (invoiceIndex, testIndex, value) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    if (hasExistingSpecimen(test)) return;
    const updatedTestData = [...testData];
    const t = updatedTestData[invoiceIndex].tests[testIndex];
    if (value && t.specimen_id && t.sample_type === "Existing") {
      const existingSampleList = existingSamples[t.specimen_id] || [];
      const selectedSample = existingSampleList.find((s) => s.sample_id === value);
      if (selectedSample) {
        updatedTestData[invoiceIndex].tests[testIndex] = {
          ...t,
          sample_id: selectedSample.sample_id,
          location: selectedSample.location,
          overall_specimen_sequence: selectedSample.overall_specimen_sequence,
        };
      }
    } else {
      t.sample_id = value;
      t.location = null;
      t.overall_specimen_sequence = null;
    }
    setTestData(updatedTestData);
  };

  const handleLocationChange = (invoiceIndex, testIndex, value) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    if (hasExistingSpecimen(test)) return;
    const updatedTestData = [...testData];
    updatedTestData[invoiceIndex].tests[testIndex].location = value;
    setTestData(updatedTestData);
  };

  const handleOverallSpecimenSequenceChange = (invoiceIndex, testIndex, value) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    if (hasExistingSpecimen(test)) return;
    const updatedTestData = [...testData];
    updatedTestData[invoiceIndex].tests[testIndex].overall_specimen_sequence = value;
    setTestData(updatedTestData);
  };

  const handleLaboratoryChange = (invoiceIndex, testIndex, value) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    if (hasExistingSpecimen(test)) return;
    const updatedTestData = [...testData];
    updatedTestData[invoiceIndex].tests[testIndex].select_is_outsource = value;
    setTestData(updatedTestData);
  };

  const getCurrentSpecimenValue = (test) => {
    if (test?.specimen_id) {
      return specimenList.find((s) => s.id === test.specimen_id) ?? null;
    }
    return null;
  };

  const getExistingSampleOptions = (test) => {
    if (test?.sample_type !== "Existing" || !test?.specimen_id) return [];
    return existingSamples[test.specimen_id] || [];
  };

  const handleSave = () => {
    if (billId) {
      const firstInvoice = testData[0];
      const firstTest = firstInvoice?.tests?.[0];
      const specimenId = firstTest?.specimen_id ?? null;
      const sampleTypeRaw = firstTest?.sample_type ?? "New";
      const sampleType = sampleTypeRaw === "Existing" ? "existing" : "new";
      const lineItems = (testData[0]?.tests || [])
        .filter((t) => t.bill_line_item_id != null)
        .map((test) => {
          const lab =
            test.select_is_outsource ??
            (test.is_outsource ? "Outsource" : "Self");
          return {
            bill_line_item_id: test.bill_line_item_id,
            laboratory: lab === "Outsource" ? "outsource" : "self",
          };
        });
      if (lineItems.length === 0) {
        errorMessage?.("No line items to save.");
        return;
      }
      const payload = {
        bill_id: billId,
        specimen_id: specimenId,
        sample_type: sampleType,
        location: commonLocation || "",
        line_items: lineItems,
      };
      setisloader(true);
      RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_SAMPLE_COLLECTION_SAVE)
        .then((response) => {
          if (response?.data?.status === "success") {
            successMessage(response?.data?.message ?? "Sample collection saved.");
            onSaveSuccess?.();
            setTimeout(() => onClose?.(), 1500);
          } else {
            errorMessage?.(response?.data?.message ?? "Failed to save.");
          }
        })
        .catch((e) => errorMessage?.(e?.response?.data?.message ?? e?.message))
        .finally(() => setisloader(false));
      return;
    }

    setisloader(true);
    const data = testData.flatMap((invoice, invIdx) =>
      (invoice.tests || [])
        .filter((test, testIdx) => {
          const testKey = `${invIdx}-${testIdx}`;
          const isSelected = selectedTests.has(testKey);
          const hasRequired = test.specimen_id && test.sample_type;
          const isNotCollected = !test.sample_collected;
          const validExisting =
            test.sample_type !== "Existing" || (test.sample_type === "Existing" && test.sample_id);
          return isSelected && hasRequired && isNotCollected && validExisting;
        })
        .map((test) => ({
          specimen_id: test.specimen_id,
          sample_type: test.sample_type,
          sample_id: test.sample_id,
          location: commonLocation,
          overall_specimen_sequence: test.overall_specimen_sequence,
          result_item_id: test.result_item_id,
          is_outsource:
            test.select_is_outsource === "Outsource"
              ? true
              : test.select_is_outsource === "Self"
                ? false
                : test.is_outsource,
        }))
        .filter((t) => t.specimen_id && t.sample_type)
    );

    if (data.length === 0) {
      errorMessage?.("Please select all mandatory fields.");
      setisloader(false);
      return;
    }

    RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_SAMPLE_DETAILS + `?patient_id=${patientId}`)
      .then((response) => {
        if (response?.data?.status === "success") {
          successMessage(response.data.message);
          const newSaved = new Set(savedTests);
          testData.forEach((invoice, invIdx) => {
            invoice.tests?.forEach((test, testIdx) => {
              const testKey = `${invIdx}-${testIdx}`;
              if (selectedTests.has(testKey) && test.specimen_id && test.sample_type) newSaved.add(testKey);
            });
          });
          setSavedTests(newSaved);
          getSampleCollectionData();
        } else {
          errorMessage?.("Failed to save: " + (response?.data?.message ?? ""));
        }
      })
      .catch((e) => errorMessage?.(e?.response?.data?.message ?? e?.message))
      .finally(() => setisloader(false));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": { minHeight: "80vh", minWidth: "90dvw" },
      }}
    >
      <DialogTitle sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0", pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
            Sample Collection
          </Typography>
          <Button
            size="small"
            variant="contained"
            sx={{ textTransform: "capitalize" }}
            startIcon={<LocalPrintshopOutlinedIcon />}
            onClick={() => handleBarcodePrint(collection?.sample_collection_record_id)}
            disabled={!collection?.sample_collection_record_id}
          >
            Print Barcode
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "#616161" }}>
            PATIENT INFORMATION
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CommonPatientDetails data={patientInfo} />
          </Box>
        </Box>

        {billSummary && (
          <Box sx={{ p: 2, backgroundColor: "#fff", borderBottom: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "#616161" }}>
              BILL SUMMARY
            </Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Typography variant="body2">
                Bill No: <strong>{billSummary.ip_op_bill_no ?? billSummary.bill_number ?? "-"}</strong>
              </Typography>
              <Typography variant="body2">
                No Of Test: <strong>{String(billSummary.no_of_tests ?? "").padStart(2, "0")}</strong>
              </Typography>
              <Typography variant="body2">
                Payment Status:{" "}
                <strong>
                  {billSummary.payment_status
                    ? billSummary.payment_status.charAt(0).toUpperCase() + billSummary.payment_status.slice(1).toLowerCase()
                    : "-"}
                </strong>
              </Typography>
            </Box>
          </Box>
        )}

        {!isloader && testData.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography>No test data found for this patient.</Typography>
          </Box>
        )}

        {!isloader &&
          testData.map((invoiceData, invoiceIndex) => (
            <Box key={invoiceIndex} sx={{ p: "11px", borderBottom: "1px solid #e0e0e0" }}>
              {!billSummary && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Typography variant="body1" sx={{ width: "25vw" }}>
                      Bill No: <strong>{invoiceData.invoice_no}</strong>
                    </Typography>
                    <Typography sx={{ width: "15vw" }}>
                      Payment Status:{" "}
                      <strong>
                        {invoiceData.payment_status
                          ? invoiceData.payment_status.charAt(0).toUpperCase() +
                            invoiceData.payment_status.slice(1).toLowerCase()
                          : "-"}
                      </strong>
                    </Typography>
                    <Typography variant="body2" sx={{ width: "15vw" }}>
                      No Of Test: <strong>{invoiceData.no_of_test}</strong>
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ textTransform: "capitalize", width: "10vw" }}
                    startIcon={<LocalPrintshopOutlinedIcon />}
                    onClick={() => handleBarcodePrint(invoiceData?.id ?? collection?.id)}
                    // disabled={!(invoiceData?.id ?? collection?.id)}
                  >
                    Print Barcode
                  </Button>
                </Box>
              )}

              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e0e0e0", width: "100%" }}>
                <Table size="small" sx={{ minWidth: "100%", tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5", "& > th": { py: 0.5, height: "2.8dvw", minHeight: "2.8dvw", lineHeight: 1.25 } }}>
                      <TableCell
                        component="th"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.8dvw",
                          width: "15vw",
                          py: 0.5,
                          verticalAlign: "middle",
                        }}
                      >
                        Test Name
                      </TableCell>
                      <TableCell
                        component="th"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.8dvw",
                          width: "10vw",
                          py: 0.5,
                          verticalAlign: "middle",
                        }}
                      >
                        Specimen
                      </TableCell>
                      <TableCell
                        component="th"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.8dvw",
                          width: "8vw",
                          py: 0.5,
                          verticalAlign: "middle",
                        }}
                      >
                        Sample type
                      </TableCell>
                      <TableCell
                        component="th"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.8dvw",
                          width: "10vw",
                          py: 0.5,
                          verticalAlign: "middle",
                        }}
                      >
                        Sample ID
                      </TableCell>
                      <TableCell
                        component="th"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.8dvw",
                          width: "12vw",
                          py: 0.5,
                          verticalAlign: "middle",
                        }}
                      >
                        Overall Specimen Sequences
                      </TableCell>
                      <TableCell
                        component="th"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.8dvw",
                          width: "8vw",
                          py: 0.5,
                          verticalAlign: "middle",
                        }}
                      >
                        Laboratory
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(invoiceData.tests || []).map((test, testIndex) => {
                      const isSelected = isTestSelected(invoiceIndex, testIndex);

                      return (
                        <TableRow key={testIndex} sx={{ "& > td": { py: 0.5, verticalAlign: "middle" } }}>
                          <TableCell
                            sx={{
                              fontSize: "0.8dvw",
                              width: "15vw",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              py: 0.5,
                            }}
                          >
                            {test.test_name ?? test.testName ?? "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8dvw", width: "10vw", py: 0.5 }}>
                            <Typography variant="body2">
                              {test.specimen_type ?? (getCurrentSpecimenValue(test)?.specimen_type ?? "-")}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8dvw", width: "8vw", py: 0.5 }}>
                            <Typography variant="body2">{test.sample_type ?? "-"}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8dvw", width: "10vw", py: 0.5 }}>
                            <Typography variant="body2">{test.sample_id ?? "-"}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8dvw", width: "12vw", py: 0.5 }}>
                            <Typography variant="body2">
                              {test.overall_specimen_sequence ?? "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8dvw", width: "8vw", py: 0.5 }}>
                            <FormControl fullWidth size="small">
                              <Select
                                size="small"
                                value={test.select_is_outsource ?? (test.is_outsource ? "Outsource" : "Self") ?? ""}
                                onChange={(e) =>
                                  handleLaboratoryChange(invoiceIndex, testIndex, e.target.value)
                                }
                                displayEmpty
                                disabled={isCheckboxDisabled(test, invoiceIndex, testIndex) || collection?.status === "Collected"}
                                sx={{ fontSize: "0.8dvw", height: "2.5dvw", minHeight: "2.5dvw" }}
                              >
                                {(!test.laboratory_options || test.laboratory_options.length === 0 || test.laboratory_options.includes("self")) && (
                                  <MenuItem value="Self">Self</MenuItem>
                                )}
                                {(!test.laboratory_options || test.laboratory_options.length === 0 || test.laboratory_options.includes("outsource")) && (
                                  <MenuItem value="Outsource">Outsource</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          backgroundColor: "#f5f5f5",
          borderTop: "1px solid #e0e0e0",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          value={commonLocation}
          onChange={(e) => setCommonLocation(e.target.value)}
          placeholder="Enter Location"
          label="Sample Location"
          disabled={collection?.status === "Collected"}
          sx={{ minWidth: "200px" }}
        />
        <Button variant="outlined" onClick={onClose} disabled={isloader} sx={{ textTransform: "capitalize" }} size="small">
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ textTransform: "capitalize" }}
          size="small"
          disabled={selectedTests.size === 0 || isloader || collection?.status === "Collected"}
        >
          Save
        </Button>
      </DialogActions>

      {message.successMsg && (
        <ToastMsg severity="success" msg={message.successMsgText} msgPop={msgClose} />
      )}
      <Loader loaderOpen={isloader} />
    </Dialog>
  );
}
